import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Edit, FileSpreadsheet, Loader2, MapPin, Plus, Save, Trash2, Truck, X } from 'lucide-react';
import { AdvancedTable } from '../components/AdvancedTable';
import { weighbridgePointService } from '../services/weighbridgePointService';
import { exportRowsToExcel } from '../utils/excelExport';
import { getGoogleMapsApiKey } from '../utils/googleMaps';
import * as XLSX from 'xlsx-js-style';

type WeighbridgePoint = {
  id: string;
  name: string;
  city: string;
  district: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
  status: 'active' | 'inactive';
  notes: string;
};

const defaultForm: WeighbridgePoint = {
  id: '',
  name: '',
  city: '',
  district: '',
  address: '',
  latitude: null,
  longitude: null,
  status: 'active',
  notes: '',
};

const toNumber = (value: unknown) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(String(value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const mapApiRow = (row: any): WeighbridgePoint => ({
  id: String(row.id),
  name: row.name || '',
  city: row.city || '',
  district: row.district || '',
  address: row.address || '',
  latitude: row.latitude != null ? Number(row.latitude) : null,
  longitude: row.longitude != null ? Number(row.longitude) : null,
  status: row.status === 'inactive' ? 'inactive' : 'active',
  notes: row.notes || '',
});

const loadGoogleMapsScript = async () => {
  const key = await getGoogleMapsApiKey();
  if (!key) throw new Error('Google Maps API key missing');
  if (window.google?.maps) return;

  await new Promise<void>((resolve, reject) => {
    const existing = document.querySelector('script[data-google-maps="true"]') as HTMLScriptElement | null;
    if (existing) {
      if (window.google?.maps) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Google Maps script load failed')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.dataset.googleMaps = 'true';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Google Maps script load failed'));
    document.head.appendChild(script);
  });
};

function WeighbridgeMapPicker({
  latitude,
  longitude,
  onChange,
}: {
  latitude: number | null;
  longitude: number | null;
  onChange: (coords: { latitude: number; longitude: number }) => void;
}) {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const clickListenerRef = useRef<any>(null);
  const [mapError, setMapError] = useState<string | null>(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    if (!mapRef.current) return;
    let cancelled = false;

    loadGoogleMapsScript()
      .then(() => {
        if (cancelled || !mapRef.current || !window.google?.maps) return;
        const center = latitude != null && longitude != null
          ? { lat: latitude, lng: longitude }
          : { lat: 39.0, lng: 35.0 };

        if (!mapInstanceRef.current) {
          mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
            center,
            zoom: latitude != null && longitude != null ? 14 : 6,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
          });
        }

        if (!markerRef.current) {
          markerRef.current = new window.google.maps.Marker({
            map: mapInstanceRef.current,
            draggable: true,
            visible: false,
          });
          markerRef.current.addListener('dragend', (event: any) => {
            const nextLat = Number(event?.latLng?.lat?.() || 0);
            const nextLng = Number(event?.latLng?.lng?.() || 0);
            onChange({ latitude: nextLat, longitude: nextLng });
          });
        }

        if (clickListenerRef.current) {
          window.google.maps.event.removeListener(clickListenerRef.current);
        }
        clickListenerRef.current = mapInstanceRef.current.addListener('click', (event: any) => {
          const nextLat = Number(event?.latLng?.lat?.() || 0);
          const nextLng = Number(event?.latLng?.lng?.() || 0);
          onChange({ latitude: nextLat, longitude: nextLng });
        });

        setMapError(null);
        setMapReady(true);
      })
      .catch((error) => {
        if (!cancelled) {
          setMapReady(false);
          const message = error instanceof Error ? error.message : 'Harita yuklenemedi';
          setMapError(message === 'Google Maps API key missing' ? 'Google Maps API key tanimli degil. Konumu manuel kaydedebilirsiniz.' : message);
        }
      });

    return () => {
      cancelled = true;
      if (clickListenerRef.current && window.google?.maps?.event) {
        window.google.maps.event.removeListener(clickListenerRef.current);
      }
    };
  }, [latitude, longitude, onChange]);

  useEffect(() => {
    if (!mapInstanceRef.current || !markerRef.current || !window.google?.maps) return;
    if (latitude == null || longitude == null) {
      markerRef.current.setVisible(false);
      return;
    }

    const position = { lat: latitude, lng: longitude };
    markerRef.current.setPosition(position);
    markerRef.current.setVisible(true);
    mapInstanceRef.current.panTo(position);
    if ((mapInstanceRef.current.getZoom?.() || 0) < 14) {
      mapInstanceRef.current.setZoom(14);
    }
  }, [latitude, longitude]);

  return (
    <div className="space-y-2">
      <div className="text-xs text-slate-500">Haritada tiklayarak veya isaretciyi surukleyerek konum secin.</div>
      <div ref={mapRef} className="h-72 w-full overflow-hidden rounded-xl border border-slate-200" />
      {!mapReady && !mapError && <div className="text-xs text-slate-500">Harita yukleniyor...</div>}
      {mapError && <div className="text-xs text-rose-600">{mapError}</div>}
    </div>
  );
}

export function WeighbridgePoints() {
  const [points, setPoints] = useState<WeighbridgePoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<WeighbridgePoint | null>(null);
  const [formData, setFormData] = useState<WeighbridgePoint>(defaultForm);
  const [latitudeInput, setLatitudeInput] = useState('');
  const [longitudeInput, setLongitudeInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isExcelImporting, setIsExcelImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const loadPoints = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response: any = await weighbridgePointService.getAll();
      setPoints(Array.isArray(response?.data) ? response.data.map(mapApiRow) : []);
    } catch (loadError: any) {
      console.error('Load weighbridge points error:', loadError);
      setError(loadError?.message || 'Kantar noktalari yuklenirken hata olustu');
      setPoints([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadPoints();
  }, []);

  const handleMapChange = useCallback(({ latitude, longitude }: { latitude: number; longitude: number }) => {
    setFormData((current) => ({ ...current, latitude, longitude }));
    setLatitudeInput(String(latitude));
    setLongitudeInput(String(longitude));
  }, []);

  const openForm = (item?: WeighbridgePoint) => {
    setEditingItem(item || null);
    setFormData(item ? { ...item } : { ...defaultForm });
    setLatitudeInput(item?.latitude != null ? String(item.latitude) : '');
    setLongitudeInput(item?.longitude != null ? String(item.longitude) : '');
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setEditingItem(null);
    setFormData({ ...defaultForm });
    setLatitudeInput('');
    setLongitudeInput('');
    setIsFormOpen(false);
  };

  const savePoint = async () => {
    if (!formData.name.trim()) {
      alert('Kantar noktasi adi zorunludur');
      return;
    }
    if (formData.latitude == null || formData.longitude == null) {
      alert('Konum secimi zorunludur');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        city: formData.city.trim() || null,
        district: formData.district.trim() || null,
        address: formData.address.trim() || null,
        latitude: formData.latitude,
        longitude: formData.longitude,
        status: formData.status,
        notes: formData.notes.trim() || null,
      };

      if (editingItem) {
        await weighbridgePointService.update(editingItem.id, payload);
      } else {
        await weighbridgePointService.create(payload);
      }

      await loadPoints();
      closeForm();
    } catch (saveError: any) {
      console.error('Save weighbridge point error:', saveError);
      alert(saveError?.message || 'Kantar noktasi kaydedilirken hata olustu');
    } finally {
      setIsSaving(false);
    }
  };

  const deletePoint = async (id: string) => {
    if (!confirm('Bu kantar noktasini silmek istediginize emin misiniz?')) return;
    try {
      await weighbridgePointService.delete(id);
      await loadPoints();
    } catch (deleteError: any) {
      console.error('Delete weighbridge point error:', deleteError);
      alert(deleteError?.message || 'Kantar noktasi silinirken hata olustu');
    }
  };

  const handleExcelImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsExcelImporting(true);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      if (rows.length === 0) {
        alert('Excel dosyasinda veri bulunamadi');
        return;
      }

      if (!confirm(`${rows.length} kantar noktasi iceri aktarilacak. Devam etmek istiyor musunuz?`)) return;

      const response: any = await weighbridgePointService.importExcel(rows);
      await loadPoints();
      const data = response?.data || {};
      alert(`Aktarim tamamlandi.\nYeni: ${data.createdCount || 0}\nGuncellenen: ${data.updatedCount || 0}\nHata: ${data.errorCount || 0}`);
    } catch (error: any) {
      console.error('Excel import error:', error);
      alert(error?.message || 'Excel aktarimi sirasinda hata olustu');
    } finally {
      setIsExcelImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const importBmsRoadInspectionStations = async () => {
    if (!confirm('BMS uzerindeki Karayolu > Denetleme Istasyonu verileriyle kantar noktalari guncellensin mi?')) return;
    setIsImporting(true);
    try {
      const response: any = await weighbridgePointService.importBmsRoadInspectionStations();
      await loadPoints();
      const summary = response?.data
        ? `\nAlinan: ${response.data.totalFetched || 0}\nYeni: ${response.data.createdCount || 0}\nGuncellenen: ${response.data.updatedCount || 0}`
        : '';
      alert(`${response?.message || 'BMS denetim istasyonlari aktarildi'}${summary}`);
    } catch (error: any) {
      console.error('Import BMS road inspection stations error:', error);
      alert(error?.message || 'BMS denetim istasyonlari aktarilirken hata olustu');
    } finally {
      setIsImporting(false);
    }
  };

  const tableData = useMemo(() => points.map((point) => ({
    id: point.id,
    name: point.name,
    city: point.city || '-',
    district: point.district || '-',
    address: point.address || '-',
    latitude: point.latitude != null ? point.latitude.toFixed(6) : '-',
    longitude: point.longitude != null ? point.longitude.toFixed(6) : '-',
    status: point.status === 'active' ? 'Aktif' : 'Pasif',
    notes: point.notes || '-',
    raw: point,
  })), [points]);

  const columns = useMemo(() => [
    { key: 'name', label: 'Kantar Noktasi' },
    { key: 'city', label: 'Il' },
    { key: 'district', label: 'Ilce' },
    { key: 'address', label: 'Adres' },
    { key: 'latitude', label: 'Enlem' },
    { key: 'longitude', label: 'Boylam' },
    {
      key: 'status',
      label: 'Durum',
      render: (value: string) => (
        <span className={`rounded-full px-2 py-1 text-xs font-medium ${value === 'Aktif' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>
          {value}
        </span>
      ),
    },
    { key: 'notes', label: 'Notlar' },
    {
      key: 'id',
      label: 'Islem',
      render: (_value: string, row: any) => (
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => openForm(row.raw)} className="rounded-lg border border-blue-200 bg-blue-50 p-2 text-blue-700 transition hover:bg-blue-100">
            <Edit className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => deletePoint(row.id)} className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-rose-700 transition hover:bg-rose-100">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ], [points]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="rounded-2xl bg-orange-100 p-3 text-orange-600">
              <Truck className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Kantar Noktalari</h1>
              <p className="mt-1 text-sm text-slate-500">Harita konumlu kantar noktalarini yonetin. Seyir mobil konumu bu noktalara 1 km yaklastiginda sistem uyarisi olusur.</p>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="file"
            accept=".xlsx,.xls"
            ref={fileInputRef}
            onChange={handleExcelImport}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => void importBmsRoadInspectionStations()}
            disabled={isImporting}
            className="inline-flex items-center gap-2 rounded-xl border border-sky-300 bg-sky-50 px-4 py-2 text-sm font-medium text-sky-700 transition hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
            BMS'den Doldur
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isExcelImporting}
            className="inline-flex items-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isExcelImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
            Excel'den Aktar
          </button>
          <button
            type="button"
            onClick={() => exportRowsToExcel(tableData as any[], `kantar-noktalari-${new Date().toISOString().slice(0, 10)}.xlsx`, { sheetName: 'WeighbridgePoints' })}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            <FileSpreadsheet className="h-4 w-4" />
            Excele Aktar
          </button>
          <button
            type="button"
            onClick={() => openForm()}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-400 px-4 py-2 text-sm font-medium text-slate-950 transition hover:bg-blue-500"
          >
            <Plus className="h-4 w-4" />
            Yeni Kantar Noktasi
          </button>
        </div>
      </div>

      {error && <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        {isLoading ? (
          <div className="flex min-h-[320px] items-center justify-center text-slate-500">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Yukleniyor...
          </div>
        ) : (
          <AdvancedTable data={tableData as any} columns={columns as any} />
        )}
      </div>

      {isFormOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 p-4">
          <div className="max-h-[94vh] w-full max-w-5xl overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">{editingItem ? 'Kantar Noktasi Duzenle' : 'Yeni Kantar Noktasi'}</h2>
                <p className="mt-1 text-sm text-slate-500">Konumu haritadan secin veya koordinatlari manuel girin.</p>
              </div>
              <button type="button" onClick={closeForm} className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid gap-6 p-6 lg:grid-cols-[1.05fr_1fr]">
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-slate-700">Kantar Noktasi Adi</label>
                    <input value={formData.name} onChange={(e) => setFormData((current) => ({ ...current, name: e.target.value }))} className="h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm outline-none focus:border-orange-400" placeholder="Orn. Gebze Kantar" />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">Il</label>
                    <input value={formData.city} onChange={(e) => setFormData((current) => ({ ...current, city: e.target.value }))} className="h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm outline-none focus:border-orange-400" placeholder="Kocaeli" />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">Ilce</label>
                    <input value={formData.district} onChange={(e) => setFormData((current) => ({ ...current, district: e.target.value }))} className="h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm outline-none focus:border-orange-400" placeholder="Gebze" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-slate-700">Adres</label>
                    <textarea value={formData.address} onChange={(e) => setFormData((current) => ({ ...current, address: e.target.value }))} rows={3} className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:border-orange-400" placeholder="Acik adres" />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">Enlem</label>
                    <input value={latitudeInput} onChange={(e) => {
                      const value = e.target.value;
                      setLatitudeInput(value);
                      setFormData((current) => ({ ...current, latitude: toNumber(value) }));
                    }} className="h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm outline-none focus:border-orange-400" placeholder="40.812345" inputMode="decimal" />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">Boylam</label>
                    <input value={longitudeInput} onChange={(e) => {
                      const value = e.target.value;
                      setLongitudeInput(value);
                      setFormData((current) => ({ ...current, longitude: toNumber(value) }));
                    }} className="h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm outline-none focus:border-orange-400" placeholder="29.412345" inputMode="decimal" />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">Durum</label>
                    <select value={formData.status} onChange={(e) => setFormData((current) => ({ ...current, status: e.target.value === 'inactive' ? 'inactive' : 'active' }))} className="h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm outline-none focus:border-orange-400">
                      <option value="active">Aktif</option>
                      <option value="inactive">Pasif</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-slate-700">Notlar</label>
                    <textarea value={formData.notes} onChange={(e) => setFormData((current) => ({ ...current, notes: e.target.value }))} rows={4} className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:border-orange-400" placeholder="Ek bilgiler" />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-700">
                    <MapPin className="h-4 w-4 text-orange-500" />
                    Harita Konum Secimi
                  </div>
                  <WeighbridgeMapPicker
                    latitude={formData.latitude}
                    longitude={formData.longitude}
                    onChange={handleMapChange}
                  />
                </div>
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  Seyir mobil konumu aktif bir kantar noktasina 1 km ve altina geldiginde admin, dispozisyon uzmanı ve planner rollerine uygulama bildirimi olusur.
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
              <button type="button" onClick={closeForm} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50">Vazgec</button>
              <button type="button" onClick={savePoint} disabled={isSaving} className="inline-flex items-center gap-2 rounded-xl bg-blue-400 px-4 py-2 text-sm font-medium text-slate-950 transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-60">
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
